/**
 * Home Layout Manager - Shared API functions with caching
 *
 * Centralizes all product/store API calls used across ConfigurationPanel,
 * CategoryProductsModal, BrandProductsModal, and PreviewPanel.
 *
 * Cache lives at module scope so it persists across React re-renders
 * and page navigations. Entries expire after a configurable TTL.
 *
 * @module HomeLayoutManager/api
 */

import { httpClient } from '../../components/fetchApiData';
import config from '../../config';
import Logger from '../../utils/Logger';
import type { Store } from './types';

const API_BASE = `${config.API_URL}/admin`;

// ---------------------------------------------------------------------------
// Cache infrastructure
// ---------------------------------------------------------------------------

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

/** Default TTL: 5 minutes */
const DEFAULT_TTL_MS = 5 * 60 * 1000;

/** Maximum cache entries — evicts oldest when exceeded */
const MAX_CACHE_SIZE = 200;

const cache = new Map<string, CacheEntry<unknown>>();

function getCached<T>(key: string, ttl = DEFAULT_TTL_MS): T | undefined {
  const entry = cache.get(key);
  if (!entry) return undefined;
  if (Date.now() - entry.timestamp > ttl) {
    cache.delete(key);
    return undefined;
  }
  return entry.data as T;
}

function setCache<T>(key: string, data: T): void {
  if (!cache.has(key) && cache.size >= MAX_CACHE_SIZE) {
    // Evict oldest entry
    let oldestKey: string | undefined;
    let oldestTime = Infinity;
    for (const [k, v] of Array.from(cache)) {
      if (v.timestamp < oldestTime) {
        oldestTime = v.timestamp;
        oldestKey = k;
      }
    }
    if (oldestKey) cache.delete(oldestKey);
  }
  cache.set(key, { data, timestamp: Date.now() });
}

/**
 * Retry a function once on failure (skips retry for 4xx client errors).
 * Returns fallback value if both attempts fail.
 */
async function retryOnce<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err: unknown) {
    const status = (err as { status?: number }).status ?? (err as { statusCode?: number }).statusCode;
    if (status && status >= 400 && status < 500) return fallback;
    await new Promise((r) => setTimeout(r, 1000));
    try {
      return await fn();
    } catch {
      return fallback;
    }
  }
}

/** In-flight request deduplication — concurrent calls to the same URL share one promise */
const inFlight = new Map<string, Promise<number>>();
const inFlightProducts = new Map<string, Promise<PreviewProduct[]>>();

/**
 * Deduplicated product count fetch.
 * If a request for the same URL is already in-flight, returns that promise.
 * Prevents React strict mode double-mounts and concurrent components from
 * firing duplicate HTTP requests (eliminates 429s without artificial delays).
 */
function deduplicatedCountFetch(url: string): Promise<number> {
  const cacheKey = `count:${url}`;
  const cached = getCached<number>(cacheKey);
  if (cached !== undefined) return Promise.resolve(cached);

  const existing = inFlight.get(url);
  if (existing) return existing;

  const promise = httpClient(url, { method: 'GET' })
    .then(({ json }) => {
      const count = (json as { total?: number; data: unknown[] }).total
        ?? (json as { data: unknown[] }).data?.length
        ?? 0;
      setCache(cacheKey, count);
      inFlight.delete(url);
      return count;
    })
    .catch(() => {
      inFlight.delete(url);
      return 0;
    });

  inFlight.set(url, promise);
  return promise;
}

/** Clear all cached data (e.g. after publish or manual refresh) */
export function clearProductCache(): void {
  cache.clear();
  inFlight.clear();
  inFlightProducts.clear();
}

/** Clear cache entries for a specific store (preserves other stores' cache for instant switch-back) */
export function clearStoreCache(storeId: string): void {
  for (const key of Array.from(cache.keys())) {
    if (key.includes(storeId)) {
      cache.delete(key);
    }
  }
}

// ---------------------------------------------------------------------------
// Stores
// ---------------------------------------------------------------------------

export async function fetchStores(): Promise<Store[]> {
  const cacheKey = 'stores';
  const cached = getCached<Store[]>(cacheKey);
  if (cached) return cached;

  try {
    const { json } = await httpClient(`${API_BASE}/stores?includeHidden=true`, { method: 'GET' });
    const stores = Array.isArray(json) ? json : ((json as { data?: Store[] }).data || []);
    setCache(cacheKey, stores);
    return stores;
  } catch (err) {
    Logger.error('Failed to fetch stores:', err);
    return [];
  }
}

// ---------------------------------------------------------------------------
// Product counts (lightweight – limit=1, only reads total)
// ---------------------------------------------------------------------------

/**
 * Fetch product count for a category based on its filter settings.
 * Used by ConfigurationPanel for the count badges.
 */
export async function fetchCategoryProductCount(
  category: {
    name: string;
    homeFilterType?: string;
    homeFilterValue?: string | null;
  },
  storeId: string | null
): Promise<number> {
  const filterType = category.homeFilterType || 'category';
  const filterValue = category.homeFilterValue || '';

  // Build params (same logic as before, but we need the key first)
  const params = new URLSearchParams();

  switch (filterType) {
    case 'tag':
      if (filterValue) {
        params.append('tags', filterValue);
      } else {
        return 0;
      }
      break;
    case 'brand':
      if (filterValue) {
        params.append('brand', filterValue);
      } else {
        return 0;
      }
      break;
    case 'newOrPopular':
      params.append('newOrPopular', 'true');
      break;
    case 'combined':
      if (filterValue) {
        try {
          const combined = JSON.parse(filterValue);
          if (combined.categories?.length > 0) {
            params.append('category', combined.categories.join(','));
          }
          if (combined.tags?.length > 0) {
            params.append('tags', combined.tags.join(','));
          }
          if (!combined.categories?.length && !combined.tags?.length) {
            return 0;
          }
        } catch {
          return 0;
        }
      } else {
        return 0;
      }
      break;
    case 'none':
      return 0;
    case 'category':
    default:
      if (filterValue) {
        params.append('category', filterValue);
      } else {
        params.append('category', category.name);
      }
      break;
  }

  if (storeId) {
    params.append('storeId', storeId);
    // Preview must keep working for a store that is switched OFF - that is exactly
    // when you lay one out. Without this the public endpoints answer with the guest
    // catalogue instead (see backend guest-browse-service).
    params.append('includeInactiveStores', 'true');
  }
  params.append('limit', '1');

  const url = `${config.API_URL}/products?${params.toString()}`;
  return deduplicatedCountFetch(url);
}

/**
 * Fetch product count for a category by name + storeId.
 * Simpler signature used by CategoryProductsModal for per-store counts.
 */
export async function fetchCategoryProductCountByName(
  categoryName: string,
  storeId: string | null
): Promise<number> {
  return fetchCategoryProductCount({ name: categoryName }, storeId);
}

/**
 * Fetch product count for a brand at a specific store.
 */
export async function fetchBrandProductCount(
  brandName: string,
  storeId: string | null
): Promise<number> {
  const params = new URLSearchParams();
  params.append('brand', brandName);
  if (storeId) {
    params.append('storeId', storeId);
    // Preview must keep working for a store that is switched OFF - that is exactly
    // when you lay one out. Without this the public endpoints answer with the guest
    // catalogue instead (see backend guest-browse-service).
    params.append('includeInactiveStores', 'true');
  }
  params.append('limit', '1');

  const url = `${config.API_URL}/products?${params.toString()}`;
  return deduplicatedCountFetch(url);
}

// ---------------------------------------------------------------------------
// Bulk product counts (single backend endpoint, replaces 30+ individual calls)
// ---------------------------------------------------------------------------

export interface ProductCountsResponse {
  categories: Record<string, number>;
  brands: Record<string, number>;
  storeId: string;
  cached: boolean;
  computedAt: string;
}

/**
 * Fetch all product counts for categories and brands at a store in one call.
 * Replaces the batch-of-3 pattern that caused 30+ individual API requests.
 * Backend uses materialized views + in-memory cache — response typically <200ms.
 */
export async function fetchAllProductCounts(
  storeId: string
): Promise<ProductCountsResponse> {
  const cacheKey = `product-counts:${storeId}`;
  const cached = getCached<ProductCountsResponse>(cacheKey);
  if (cached) return cached;

  try {
    const { json } = await httpClient(
      `${API_BASE}/home-layout/product-counts?storeId=${storeId}`,
      { method: 'GET' }
    );
    const result = json as ProductCountsResponse;
    setCache(cacheKey, result);
    return result;
  } catch (err) {
    Logger.error('Failed to fetch product counts:', err);
    return { categories: {}, brands: {}, storeId, cached: false, computedAt: new Date().toISOString() };
  }
}

// ---------------------------------------------------------------------------
// Full product lists (used by modals to show product tables)
// ---------------------------------------------------------------------------

interface ProductBase {
  id: string;
  name: string;
  price: number;
  priceTier1?: number;
  stock: number;
  sku?: string;
  barcode?: string;
  status?: string;
  isNew?: boolean;
  isPopular?: boolean;
  isRestricted?: boolean;
  isSellable?: boolean;
  isSellableInApp?: boolean;
  attachments?: Array<{
    id?: string;
    url?: string;
    thumbnailUrl?: string;
    downloadUrl?: string;
    type?: string;
  }>;
  brand?: string | { name: string };
  tags?: Array<string | { name: string }>;
  description?: string;
}

/**
 * Fetch products for a category at a specific store.
 */
export async function fetchCategoryProducts(
  categoryName: string,
  storeId: string | null,
  limit = 100
): Promise<ProductBase[]> {
  const params = new URLSearchParams();
  params.append('category', categoryName);
  params.append('limit', String(limit));
  if (storeId) params.append('storeId', storeId);
  // See the note on includeInactiveStores above.
  if (storeId) params.append('includeInactiveStores', 'true');

  const url = `${config.API_URL}/products?${params.toString()}`;
  const cacheKey = `products:${url}`;
  const cached = getCached<ProductBase[]>(cacheKey);
  if (cached) return cached;

  try {
    const { json } = await httpClient(url, { method: 'GET' });
    const products = (json as { data: ProductBase[] }).data || [];
    setCache(cacheKey, products);
    return products;
  } catch (err) {
    Logger.error(`Failed to fetch products for category ${categoryName}:`, err);
    return [];
  }
}

export interface PaginatedProductsResponse {
  data: ProductBase[];
  total: number;
  page: number;
}

/**
 * Fetch products for a category with pagination and server-side search.
 * Used by CategoryProductsModal for infinite scroll + search.
 */
export async function fetchCategoryProductsPaginated(
  categoryName: string,
  storeId: string | null,
  options: { page?: number; limit?: number; search?: string } = {}
): Promise<PaginatedProductsResponse> {
  const { page = 1, limit = 50, search } = options;
  const params = new URLSearchParams();
  params.append('category', categoryName);
  params.append('limit', String(limit));
  params.append('page', String(page));
  if (storeId) params.append('storeId', storeId);
  // See the note on includeInactiveStores above.
  if (storeId) params.append('includeInactiveStores', 'true');
  if (search) params.append('name', search);

  const url = `${config.API_URL}/products?${params.toString()}`;
  const cacheKey = `products-pag:${url}`;
  const cached = getCached<PaginatedProductsResponse>(cacheKey);
  if (cached) return cached;

  try {
    const { json } = await httpClient(url, { method: 'GET' });
    const result: PaginatedProductsResponse = {
      data: (json as { data: ProductBase[] }).data || [],
      total: (json as { total?: number }).total ?? 0,
      page,
    };
    setCache(cacheKey, result);
    return result;
  } catch (err) {
    Logger.error(`Failed to fetch products for category ${categoryName}:`, err);
    return { data: [], total: 0, page };
  }
}

/**
 * Fetch products for a brand at a specific store.
 */
export async function fetchBrandProducts(
  brandName: string,
  storeId: string | null,
  limit = 100
): Promise<ProductBase[]> {
  const params = new URLSearchParams();
  params.append('brand', brandName);
  params.append('limit', String(limit));
  if (storeId) params.append('storeId', storeId);
  // See the note on includeInactiveStores above.
  if (storeId) params.append('includeInactiveStores', 'true');

  const url = `${config.API_URL}/products?${params.toString()}`;
  const cacheKey = `products:${url}`;
  const cached = getCached<ProductBase[]>(cacheKey);
  if (cached) return cached;

  try {
    const { json } = await httpClient(url, { method: 'GET' });
    const products = (json as { data: ProductBase[] }).data || [];
    setCache(cacheKey, products);
    return products;
  } catch (err) {
    Logger.error(`Failed to fetch products for brand ${brandName}:`, err);
    return [];
  }
}

// ---------------------------------------------------------------------------
// Preview products (filter-aware, cached, deduplicated — used by PreviewPanel)
// ---------------------------------------------------------------------------

/** Minimal product type for preview rendering */
export interface PreviewProduct {
  id: string;
  name: string;
  price: number;
  priceTier1?: number;
  stock: number;
  sku?: string;
  barcode?: string;
  description?: string;
  brand?: string | { name: string };
  tags?: Array<string | { name: string }>;
  status?: string;
  isNew?: boolean;
  isPopular?: boolean;
  isRestricted?: boolean;
  isSellable?: boolean;
  isSellableInApp?: boolean;
  attachments?: Array<{
    id?: string;
    url?: string;
    thumbnailUrl?: string;
    downloadUrl?: string;
    type?: string;
  }>;
}

/**
 * Build URLSearchParams for a category's filter settings.
 * Shared by fetchPreviewProducts and fetchCategoryProductCount.
 */
function buildFilterParams(
  category: { name: string; homeFilterType?: string; homeFilterValue?: string | null },
  storeId: string | null,
  limit: number
): URLSearchParams | null {
  const filterType = category.homeFilterType || 'category';
  const filterValue = category.homeFilterValue || '';
  const params = new URLSearchParams();

  switch (filterType) {
    case 'tag':
      if (filterValue) {
        params.append('tags', filterValue);
      } else {
        return null;
      }
      break;
    case 'brand':
      if (filterValue) {
        params.append('brand', filterValue);
      } else {
        return null;
      }
      break;
    case 'newOrPopular':
      params.append('newOrPopular', 'true');
      break;
    case 'combined':
      if (filterValue) {
        try {
          const combined = JSON.parse(filterValue);
          if (combined.categories?.length > 0) {
            params.append('category', combined.categories.join(','));
          }
          if (combined.tags?.length > 0) {
            params.append('tags', combined.tags.join(','));
          }
          if (!combined.categories?.length && !combined.tags?.length) {
            return null;
          }
        } catch {
          return null;
        }
      } else {
        return null;
      }
      break;
    case 'none':
      return null;
    case 'category':
    default:
      if (filterValue) {
        params.append('category', filterValue);
      } else {
        params.append('category', category.name);
      }
      break;
  }

  if (storeId) params.append('storeId', storeId);
  // See the note on includeInactiveStores above.
  if (storeId) params.append('includeInactiveStores', 'true');
  params.append('limit', String(limit));
  return params;
}

/**
 * Fetch products for a category based on its filter settings.
 * Cached + deduplicated to prevent concurrent duplicate requests.
 */
export async function fetchPreviewProducts(
  category: { name: string; homeFilterType?: string; homeFilterValue?: string | null },
  storeId: string | null,
  limit = 4
): Promise<PreviewProduct[]> {
  const params = buildFilterParams(category, storeId, limit);
  if (!params) return [];

  const url = `${config.API_URL}/products?${params.toString()}`;
  const cacheKey = `preview:${url}`;

  const cached = getCached<PreviewProduct[]>(cacheKey);
  if (cached) return cached;

  const existing = inFlightProducts.get(cacheKey);
  if (existing) return existing;

  const promise = retryOnce(
    () => httpClient(url, { method: 'GET' }).then(({ json }) => {
      const products = (json as { data: PreviewProduct[] }).data || [];
      return products;
    }),
    [] as PreviewProduct[]
  )
    .then((products) => {
      setCache(cacheKey, products);
      inFlightProducts.delete(cacheKey);
      return products;
    })
    .catch((err) => {
      inFlightProducts.delete(cacheKey);
      Logger.error(`Failed to fetch preview products for ${category.name}:`, err);
      return [] as PreviewProduct[];
    });

  inFlightProducts.set(cacheKey, promise);
  return promise;
}

/**
 * Fetch New & Popular products (convenience wrapper).
 */
export async function fetchNewAndPopularProducts(
  storeId: string | null,
  limit = 4
): Promise<PreviewProduct[]> {
  return fetchPreviewProducts({ name: 'New & Popular', homeFilterType: 'newOrPopular' }, storeId, limit);
}

/**
 * Fetch preview products for multiple categories with concurrency limit.
 * Prevents 15-20 simultaneous HTTP requests — limits to `concurrency` at a time.
 */
export async function fetchPreviewProductsBatch(
  categories: Array<{ id: string; name: string; homeFilterType?: string; homeFilterValue?: string | null }>,
  storeId: string | null,
  options: { limit?: number; concurrency?: number } = {}
): Promise<Map<string, PreviewProduct[]>> {
  const { limit = 1, concurrency = 4 } = options;
  const results = new Map<string, PreviewProduct[]>();

  // Process in batches of `concurrency`
  for (let i = 0; i < categories.length; i += concurrency) {
    const batch = categories.slice(i, i + concurrency);
    const batchResults = await Promise.all(
      batch.map(async (cat) => {
        const products = await fetchPreviewProducts(cat, storeId, limit);
        return { id: cat.id, products };
      })
    );
    for (const { id, products } of batchResults) {
      results.set(id, products);
    }
  }

  return results;
}

// ---------------------------------------------------------------------------
// Brands
// ---------------------------------------------------------------------------

export interface Brand {
  id: string;
  name: string;
  imageUrl?: string;
  /** Live ordering column. Used as the sort fallback for brands absent from the draft. */
  position?: number;
  /**
   * Whether this brand has a sellable product at the requested store — only
   * present when `fetchBrands` is called with a storeId.
   *
   * Computed server-side by the SAME query the mobile home screen uses, so
   * filtering on it cannot disagree with the app. Do NOT re-derive this by
   * counting products per brand: that is what previously made the preview
   * disagree with both the app and the ordering screen.
   */
  hasProductsAtStore?: boolean;
  /**
   * Whether this brand has a sellable product at ANY store. Always returned.
   *
   * This is what the Content Control preview filters on: that screen has no
   * "view as a store" control, so it cannot ask about one store, but it still
   * shouldn't render brand records with nothing behind them anywhere.
   */
  hasProducts?: boolean;
}

/** Keyed by store, because `hasProductsAtStore` differs per store. */
const brandsCache = new Map<string, Brand[]>();

/**
 * Fetch all brands, ordered by their live position.
 *
 * Pass a store's EXTERNAL id to have the backend stamp `hasProductsAtStore` on
 * each brand. Simple in-memory cache, no TTL — brands rarely change.
 */
export async function fetchBrands(storeId?: string | null): Promise<Brand[]> {
  const key = storeId || '__all__';
  const cached = brandsCache.get(key);
  if (cached) return cached;

  try {
    const params = new URLSearchParams({ limit: '100' });
    if (storeId) params.append('storeId', storeId);
    const url = `${config.API_URL}/brands?${params.toString()}`;
    const { json } = await httpClient(url, { method: 'GET' });
    const brands = (json as { data: Brand[] }).data || [];
    brandsCache.set(key, brands);
    return brands;
  } catch (err) {
    Logger.error('Failed to fetch brands:', err);
    return [];
  }
}

/**
 * Drop every cached brand list.
 *
 * `brandsCache` has NO TTL, so anything that changes a brand must call this or
 * the rail keeps serving the list from before the change — including the
 * per-store copies, since a new brand can affect any of them.
 */
export function clearBrandsCache(): void {
  brandsCache.clear();
}

/**
 * Create a brand. The logo is required — the API rejects a brand without one,
 * because an imageless brand renders as a blank tile in the app.
 *
 * The new brand is appended to the end of the rail; ordering is a separate
 * drag-and-drop step.
 */
export async function createBrand(name: string, image: File): Promise<Brand> {
  const form = new FormData();
  form.append('name', name);
  form.append('image', image);

  // No Content-Type header: the browser has to set the multipart boundary
  // itself, and ra-core's fetchJson leaves FormData bodies alone.
  const { json } = await httpClient(`${API_BASE}/brands`, { method: 'POST', body: form });
  clearBrandsCache();
  return json as Brand;
}

/** Rename a brand and/or replace its logo. Send only what changed. */
export async function updateBrand(
  id: string,
  changes: { name?: string; image?: File | null },
): Promise<Brand> {
  const form = new FormData();
  if (changes.name !== undefined) form.append('name', changes.name);
  if (changes.image) form.append('image', changes.image);

  const { json } = await httpClient(`${API_BASE}/brands/${id}`, { method: 'PATCH', body: form });
  clearBrandsCache();
  return json as Brand;
}

/**
 * Delete a brand. Products keep their `brand` string — this only removes the
 * rail entry, so nothing in the catalogue is orphaned.
 */
export async function deleteBrand(id: string): Promise<void> {
  await httpClient(`${API_BASE}/brands/${id}`, { method: 'DELETE' });
  clearBrandsCache();
}
